import type { ExperimentDetail } from '~/features/experiments/types/experiment.types';
import type { AppliedProductFilters } from '~/features/experiments/types/product-metadata.types';

/** Wire shape the overview endpoint expects — single string for scalar filters, array for multi-select. */
export type OverviewFilters = Record<string, string | string[]>;

/**
 * Combine an experiment's scope + grouping filters into the value-typed
 * map the catalog-overview endpoint expects.
 *
 * Filters are now `AppliedProductFilters` (no comma-joining at the boundary),
 * so values are passed through as-is: `string[]` → `string[]`, scalars
 * (string/number/boolean) → `string`. Null values are skipped.
 *
 * Precedence per key: `groupingFilter.filters` wins over `scopingFilters`,
 * mirroring `mergeAppliedFilters` — grouping narrows scope, never widens it.
 */
export function combineExperimentFiltersForOverview(
  experiment: Pick<ExperimentDetail, 'scopingFilters' | 'groupingFilter'>,
): OverviewFilters {
  const merged: AppliedProductFilters = {
    ...(experiment.scopingFilters ?? {}),
    ...(experiment.groupingFilter?.filters ?? {}),
  };

  const out: OverviewFilters = {};
  for (const [key, value] of Object.entries(merged)) {
    if (value === null) continue;
    if (Array.isArray(value)) {
      const parts = value.filter((v) => v !== '');
      if (parts.length > 0) out[key] = parts;
    } else {
      const str = String(value).trim();
      if (str.length > 0) out[key] = str;
    }
  }
  return out;
}
